import React from 'react';
import styled from '@emotion/styled';
import { ReactComponent as ClockImage } from '../../assets/Clock.svg';
import Tooltip from '../../components/tooltipV2/Tooltip';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import Table from '../../components/table';
import { Trans, useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

type EarlyPurchaseContentProps = {
  itemsWithTax: string;
  ipWithTax: string;
  cashPlusPrice: string;
  cashPlusFee: string;
  cashPlusTerm: { singular: string; plural: string };
  itemsTotal: string;
  salesTax: string;
  isVirtualCard: boolean;
};

const EarlyPurchaseContainer = styled.div`
  margin-top: 1rem;
  font-family: ${fontFamilies.interLight};
  border-radius: 4px;
  border: 1px solid #dee1e1;
  padding: 1rem;
`;
const EarlyPurchaseHeader = styled.div`
  color: ${colors.darkBlue};
  font-family: ${fontFamilies.gilroyBold};
  font-size: 1.25rem;
  line-height: 125%;
  text-align: left;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
`;

const SubtextStyle = styled.div`
  color: ${colors.darkBlue};
  font-family: ${fontFamilies.interLight};
  font-size: 0.8rem;
  line-height: 125%;
  text-align: left;
  margin-bottom: 10px;
  & strong,
  & a {
    font-family: ${fontFamilies.interSemiBold};
  }
`;

const Spacer = styled.div`
  height: 1rem;
`;

const doNotQualifyTooltip = (cpt: string, t: TFunction) => (
  <div>
    {t(($) => $.pageContractSummaryEcomm.doNotQualifyTooltip, {
      cpt,
    })}
  </div>
);

const retailPriceTooltip = (t: TFunction) => <div>{t(($) => $.pageContractSummaryEcomm.retailPriceTooltip)}</div>;

const salesTaxTooltip = (t: TFunction) => <div>{t(($) => $.pageContractSummaryEcomm.salesTaxTooltip)}</div>;

const initialPaymentTooltip = (t: TFunction) => <div>{t(($) => $.pageContractSummaryEcomm.initialPaymentTooltip)}</div>;

const getLabel = (labelText: string, tooltipID: string, tooltipContent: any) => {
  return (
    <div key={tooltipID}>
      {labelText} <Tooltip id={tooltipID} tooltipContent={tooltipContent} inline={true} size="12px" />
    </div>
  );
};

const EarlyPurchaseContent: React.FC<EarlyPurchaseContentProps> = ({
  ipWithTax,
  cashPlusPrice,
  cashPlusFee,
  salesTax,
  itemsTotal,
  itemsWithTax,
  cashPlusTerm,
  isVirtualCard,
}) => {
  const { t } = useTranslation();
  // Tooltips
  const earlyPurchaseFeeTooltip = (
    <div>
      {t(($) => $.pageContractSummaryEcomm.earlyPurchaseFee, {
        cashPlusTermPlural: cashPlusTerm.plural,
      })}
    </div>
  );
  const earlyPurchaseAmountTooltip = (
    <div>
      {t(($) => $.pageContractSummaryEcomm.payInFull, {
        cashPlusTermPlural: cashPlusTerm.plural,
        cashPlusPrice,
        itemsWithTax,
        ipWithTax,
        cashPlusFee,
      })}
    </div>
  );

  const tableContent = [
    ...(!isVirtualCard
      ? [
          {
            label: getLabel(
              t(($) => $.pageContractSummaryEcomm.retailPrice),
              'ebo-retailPrice',
              retailPriceTooltip(t),
            ),
            value: itemsTotal,
          },
        ]
      : []),
    ...(!isVirtualCard
      ? [
          {
            label: getLabel(
              t(($) => $.pageContractSummaryEcomm.salesTax),
              'ebo-salesTax',
              salesTaxTooltip(t),
            ),
            value: salesTax,
          },
        ]
      : []),
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.initialPayment),
        'ebo-initialPayment',
        initialPaymentTooltip(t),
      ),
      value: ipWithTax,
    },
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.earlyPurchaseFeeLabel),
        'ebo-earlyPurchaseFee',
        earlyPurchaseFeeTooltip,
      ),
      value: cashPlusFee,
    },
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.earlyPurchaseAmount),
        'ebo-earlyPurchaseAmount',
        earlyPurchaseAmountTooltip,
      ),
      value: cashPlusPrice,
      makeBold: true,
    },
  ];
  return (
    <EarlyPurchaseContainer>
      <ClockImage />
      <EarlyPurchaseHeader>{t(($) => $.pageContractSummaryEcomm.earlyPurchaseOptions)}</EarlyPurchaseHeader>
      <SubtextStyle>
        <Trans
          i18nKey={($) => $.pageContractSummaryEcomm.earlyPurchaseOptionExplanation}
          values={{ cashPlusTermPlural: cashPlusTerm.plural }}
          components={{ strong: <strong /> }}
        />
        <Tooltip id="ebo-subheader-tooltip" tooltipContent={earlyPurchaseAmountTooltip} inline={true} size="12px" />
      </SubtextStyle>
      <Table name="early-purchase-table" rows={tableContent} />
      <Spacer />
      <SubtextStyle>
        <strong>
          {t(($) => $.pageContractSummaryEcomm.doNotQualify, {
            cashPlusTermPlural: cashPlusTerm.plural,
          })}
        </strong>
        , {t(($) => $.pageContractSummaryEcomm.payOffEarly)}
        <Tooltip id="earlyBuyout" tooltipContent={doNotQualifyTooltip(cashPlusTerm.singular, t)} inline={true} size="12px" />
      </SubtextStyle>
    </EarlyPurchaseContainer>
  );
};

export default EarlyPurchaseContent;
